import React, { useEffect } from 'react';
import { List } from 'immutable';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import MatLayout from '../Board/MatLayout';
import { pictureSpot } from '../Board/pictureSpot';
import { selectCardsById } from '../Cards/selectors';
import { describeEntry } from '../GameLog/describeEntry';
import LogText from '../GameLog/LogText';
import Avatar from '../Avatar';
import { Muted } from '../Controls';
import { watchGame } from './actions';
import { Moves, Players, PlayerLine, WatchBoard, WatchPage } from './styled';

/** How many of the latest moves show under the table. */
const RECENT_MOVES = 12;

/**
 * Someone else's game, live: the table as anyone may see it (never a hand, a deck or the prizes), the players, and
 * the latest moves by name. The first player in the game sits at the bottom.
 */
const Watch = () => {
  const { id } = useParams();
  const gameId = Number(id);
  const dispatch = useDispatch();
  const game = useSelector((state) => state.watch.get('game'));
  const entries = useSelector((state) => state.watch.get('log'));
  const cardsById = useSelector(selectCardsById);

  useEffect(() => {
    dispatch(watchGame(gameId));
  }, [dispatch, gameId]);

  if (!game || game.get('id') !== gameId) {
    return <WatchPage><Muted>Loading the game…</Muted></WatchPage>;
  }

  const users = game.get('users') || List();
  const [bottom, top] = [users.get(0), users.get(1)];
  const names = Object.fromEntries(users.map(user => [user.get('id'), user.get('name')]).toArray());
  const board = game.get('board') || List();
  const side = (user) => user && board.find(s => s.get('user_id') === user.get('id'));
  // Who won, by name: nobody here is "you".
  const ended = game.get('ended_at') ? `${names[game.get('winner_id')] || 'Nobody'} won` : null;
  const moves = (entries || []).slice(-RECENT_MOVES).reverse()
    .map(entry => ({ entry, lines: describeEntry(entry, { me: null, cardsById, names }) }))
    .filter(({ lines }) => lines.length);

  const player = (user) => {
    const s = side(user);
    return user && (
      <PlayerLine>
        <Avatar user={user} size={24} game={gameId} />
        <strong>{user.get('name')}</strong>
        {game.get('active_player_id') === user.get('id') && !game.get('ended_at') && <span>· their turn</span>}
        {s && <Muted as="span">· {s.get('prizes').size} prizes left · hand {s.get('hand')} · deck {s.get('deck')}</Muted>}
      </PlayerLine>
    );
  };

  return (
    <WatchPage>
      <Link to="/games">‹ Games</Link>
      <Players>
        {player(top)}
        {ended && <strong>{ended}</strong>}
      </Players>
      <WatchBoard>
        <MatLayout turned={false} opponentSpot={pictureSpot(side(top), cardsById)} playerSpot={pictureSpot(side(bottom), cardsById)} />
      </WatchBoard>
      <Players>{player(bottom)}</Players>
      <Moves aria-label="Latest moves">
        {moves.map(({ entry, lines }) => (
          <li key={entry.id}>
            {lines.map((line, i) => <div key={i}><LogText text={line.text} /></div>)}
          </li>
        ))}
      </Moves>
    </WatchPage>
  );
};

export default Watch;
